import React from 'react';

import { SelectedAddOns } from '@/components/common/ProductAddToCart/ProductAddToCart';

import { ExtendedProductFields } from '@/core/contentfulConfig';
import { TypeProductFields } from '@/core/generated-types';

import { serializeCartLines } from '@/hooks/usePlayGeniusCheckout/utils/cartHelpers';

import { calculateCartTotal } from '@/lib/cart/helpers';

import { getKeyValueLists } from '@/lib/contentful';

import {
  Cart,
  ShopifyProduct,
  ShopifyProductType,
  ShopifyVariant,
} from '@/lib/shopify';

import { captureProductFieldsEvent, pushEvent } from '@/utils/dataLayer';

import {
  PRODUCT_ADDED_COMPONENT_TYPES,
  PRODUCT_EVENT_TYPES,
  PRODUCT_TYPES,
  track,
} from '@/utils/rudderstack';

export interface HandleCartFormSubmitProps {
  e: React.FormEvent<HTMLFormElement>;
  shopifyProduct: ShopifyProduct;
  shopifyVariant: ShopifyVariant;
  productFields: TypeProductFields;
  defaultQuantity: number;
  buttonLevel:
    | PRODUCT_ADDED_COMPONENT_TYPES.PRIMARY_CTA
    | PRODUCT_ADDED_COMPONENT_TYPES.FULL_WIDTH_PRODUCT_CARD;
  url?: string;
  selectedAddOns?: SelectedAddOns[];
  onSubmitSuccess?: () => void;
  onSubmitFailure?: () => void;
  onStartFormSubmission?: () => void;
  noRedirect?: boolean;
  locale: string;
}

export async function handleCartFormSubmit(props: HandleCartFormSubmitProps) {
  const {
    e,
    noRedirect,
    shopifyProduct,
    onSubmitSuccess,
    onSubmitFailure,
    onStartFormSubmission,
    locale,
  } = props;

  onStartFormSubmission?.();

  try {
    e.preventDefault();
    captureAddToCartEvents(props);
  } catch (error) {
    console.error('Error encountered tracking add to cart events', error);
  }

  if (noRedirect) {
    const formData = new FormData(e.currentTarget);

    const isERCSubscriptionProduct =
      shopifyProduct.productType === ShopifyProductType.EARLY_READER_CLUB;

    // If we are adding an ERC subscription product to the cart, we will first remove
    // any existing ERC subscriptions from the cart to enforce a limit of one per cart.
    if (isERCSubscriptionProduct) {
      const cart: Cart = await fetch('/api/cart', { cache: 'no-cache' })
        .then((res) => {
          if (!res.ok) {
            console.error('Failed to fetch cart');
          }

          return res.json();
        })
        .catch((e) => console.error(e));

      // Find existing ERC products in the cart, and if any are found, remove them.
      const existingErcItems =
        cart?.lines?.edges?.filter(({ node }) => {
          const productType = node.merchandise?.product?.productType;
          return productType === ShopifyProductType.EARLY_READER_CLUB;
        }) || [];

      const { configDynamicBusinessValues } = (await getKeyValueLists([
        'configDynamicBusinessValues',
      ])) as { configDynamicBusinessValues: Record<string, string> };

      const MAX_ERC_PER_CART =
        +configDynamicBusinessValues.MAX_ERC_PER_CART || 3;

      const maxErcPerCartLimit = MAX_ERC_PER_CART - 1;

      if (existingErcItems.length > maxErcPerCartLimit) {
        const serializedCartItems = await serializeCartLines(
          cart.lines.edges,
          locale,
        );

        track(PRODUCT_EVENT_TYPES.PRODUCT_LIMIT_REACHED, {
          limit: MAX_ERC_PER_CART,
          productType: PRODUCT_TYPES.ERC_SUBSCRIPTION,
          url: window.location.pathname,
          cartTotal: calculateCartTotal(serializedCartItems),
        });

        const variantIdsToRemove = existingErcItems
          .slice(maxErcPerCartLimit)
          .map(({ node }) => node.merchandise.id);

        try {
          const res = await fetch('/api/cart/remove', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ lineIds: variantIdsToRemove }),
          });

          if (!res.ok) {
            console.error(
              'Error encountered removing existing ERC items from cart',
            );
          }
        } catch (e) {
          console.error(
            'Error encountered removing existing ERC items from cart',
            e,
          );
        }
      }
    }

    const addToCartResponse = await fetch('/api/cart/add?noredirect=1', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.fromEntries(formData.entries())),
    });

    if (addToCartResponse.ok) {
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } else {
      console.error(
        'Error encountered submitting cart',
        addToCartResponse.json(),
      );

      if (onSubmitFailure) {
        onSubmitFailure();
      }
    }
  } else {
    e.currentTarget.submit();
  }
}

function captureAddToCartEvents({
  e,
  shopifyProduct,
  shopifyVariant,
  defaultQuantity,
  buttonLevel,
  productFields,
  url,
  selectedAddOns,
}: HandleCartFormSubmitProps) {
  const formData = new FormData(e.currentTarget);

  const qtyVal = Number(formData.get('quantity') || defaultQuantity);

  const urlOrFallback = url || '';

  pushEvent('addToCart', { qtyVal, shopifyProduct, variant: shopifyVariant });

  captureProductFieldsEvent(
    PRODUCT_EVENT_TYPES.PRODUCT_ADDED,
    {
      shopifyProduct,
      variant: shopifyVariant,
      product: productFields,
      quantity: qtyVal,
      url: urlOrFallback,
    },
    buttonLevel,
  );

  selectedAddOns?.forEach((selectedAddOn) => {
    const shopifyProductAddOn = (selectedAddOn?.fields as ExtendedProductFields)
      ?.shopifyProduct;

    if (shopifyProductAddOn) {
      pushEvent('addToCart', {
        qtyVal: 1,
        shopifyProduct: shopifyProductAddOn as ShopifyProduct,
        variant: selectedAddOn.variant,
      });

      captureProductFieldsEvent(
        PRODUCT_EVENT_TYPES.PRODUCT_ADDED,
        {
          shopifyProduct: shopifyProductAddOn as ShopifyProduct,
          variant: selectedAddOn.variant,
          product: productFields,
          quantity: 1,
          url: urlOrFallback,
        },
        PRODUCT_ADDED_COMPONENT_TYPES.UPSELL_PDP,
      );
    }
  });
}
